import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { fmtDMY, todayNZ } from "@/lib/format";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { BedDouble, Plus, Trash2, Loader2, CalendarClock } from "lucide-react";

const PRESETS = [
  { name: "Change sheets", every_days: 7, minutes: 10 },
  { name: "Clean bathroom thoroughly", every_days: 7, minutes: 10 },
  { name: "Vacuum & mop all floors", every_days: 7, minutes: 5 },
  { name: "Change towels", every_days: 3, minutes: 3 },
  { name: "Clean kitchen thoroughly", every_days: 7, minutes: 10 },
  { name: "Empty rubbish & recycling", every_days: 3, minutes: 2 },
];
const DEFAULT_COUNT = 3; // first three presets are pre-added to a new stay

const newId = () => Math.random().toString(36).slice(2) + Date.now().toString(36);

export function StayDialog({ open, onClose, roomNumber, stay, guestName, date }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [room, setRoom] = useState("");
  const [checkIn, setCheckIn] = useState("");
  const [checkOut, setCheckOut] = useState("");
  const [tasks, setTasks] = useState([]);
  const { data: rooms = [] } = useQuery({
    queryKey: ["rooms"],
    queryFn: async () => (await api.get("/rooms")).data,
    enabled: open,
  });

  useEffect(() => {
    if (!open) return;
    setName(stay?.guest_name || guestName || "");
    setRoom(stay?.room_number || roomNumber || "");
    setCheckIn(stay?.check_in || date || todayNZ());
    setCheckOut(stay?.ended ? "" : stay?.check_out || "");
    setTasks(stay?.tasks?.length ? stay.tasks.map((x) => ({ ...x })) : PRESETS.slice(0, DEFAULT_COUNT).map((p) => ({ id: newId(), ...p })));
  }, [open, stay, guestName, date, roomNumber]);

  const done = () => {
    qc.invalidateQueries({ queryKey: ["stays"] });
    qc.invalidateQueries({ queryKey: ["cleans"] });
    onClose();
  };
  const save = useMutation({
    mutationFn: () => {
      const body = { room_number: room.trim(), guest_name: name, check_in: checkIn, check_out: checkOut, tasks };
      return stay ? api.put(`/stays/${stay.id}`, body) : api.post("/stays", body);
    },
    onSuccess: () => { toast.success(stay?.ended ? t("Long stay reopened") : t("Long stay saved — tasks will appear on the service days they're due")); done(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });
  const remove = useMutation({
    mutationFn: () => api.delete(`/stays/${stay.id}`),
    onSuccess: () => { toast.success(t("Long stay removed")); done(); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  const setTask = (i, patch) => setTasks((ts) => ts.map((x, j) => (j === i ? { ...x, ...patch } : x)));
  const addPreset = (p) => {
    if (tasks.some((x) => x.name.toLowerCase() === p.name.toLowerCase())) return;
    setTasks((ts) => [...ts, { id: newId(), ...p }]);
  };
  const valid = room.trim() && checkIn && checkOut && checkOut >= checkIn && tasks.every((x) => x.name.trim() && Number(x.every_days) >= 1);
  const nights = checkIn && checkOut ? Math.max(0, Math.round((new Date(checkOut) - new Date(checkIn)) / 86400000)) : 0;
  const upcoming = checkIn && checkIn > todayNZ();

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto" data-testid="stay-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><BedDouble className="h-5 w-5" /> {t("Long stay")}{room ? ` — ${t("Room")} ${room}` : ""}</DialogTitle>
          <DialogDescription>
            {t("Set the guest's stay dates and what needs doing every few days. Due items are added automatically to that day's service for the cleaner.")}
            {stay && !stay.ended ? " " + t("Change a task's next-due date to move it to a better-staffed day.") : ""}
            {!stay ? " " + t("A future check-in date is fine — you'll be asked to confirm the room and dates when that day's CSV is uploaded.") : ""}
          </DialogDescription>
        </DialogHeader>

        {stay?.ended && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-900" data-testid="stay-ended-note">
            {stay.closed_by_departure
              ? t("This stay was ended on {{date}} by a departure clean. Set a new check-out date to reopen it, or remove it.", { date: fmtDMY(stay.closed_by_departure) })
              : t("This stay ended on {{date}}. Set a new check-out date to reopen it, or remove it.", { date: fmtDMY(stay.check_out) })}
          </div>
        )}

        <div className="grid gap-3 sm:grid-cols-3">
          <div className="space-y-1.5">
            <Label>{t("Room")}</Label>
            <Input list="stay-room-options" value={room} onChange={(e) => setRoom(e.target.value)} placeholder={t("Room number")} data-testid="stay-room" />
            <datalist id="stay-room-options">
              {rooms.filter((r) => r.active !== false).map((r) => <option key={r.room_number} value={r.room_number} />)}
            </datalist>
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label>{t("Guest name")}</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} data-testid="stay-guest-name" />
          </div>
          <div className="space-y-1.5">
            <Label>{t("Check-in")}</Label>
            <Input type="date" value={checkIn} onChange={(e) => setCheckIn(e.target.value)} data-testid="stay-check-in" />
          </div>
          <div className="space-y-1.5">
            <Label>{t("Check-out")}</Label>
            <Input type="date" value={checkOut} min={checkIn} onChange={(e) => setCheckOut(e.target.value)} data-testid="stay-check-out" />
          </div>
          <div className="flex items-end pb-2 text-sm text-muted-foreground" data-testid="stay-nights">
            {nights ? t("{{count}} nights", { count: nights }) : ""}
            {upcoming && <span className="ml-2 rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-semibold text-indigo-800" data-testid="stay-upcoming-badge">{t("Upcoming")}</span>}
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="label-caps text-muted-foreground">{t("During the stay")}</Label>
            <Button variant="outline" size="sm" className="h-7 gap-1 text-xs" onClick={() => setTasks((ts) => [...ts, { id: newId(), name: "", every_days: 3, minutes: 5 }])} data-testid="stay-add-task">
              <Plus className="h-3.5 w-3.5" /> {t("Add")}
            </Button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {PRESETS.map((p) => (
              <button
                key={p.name}
                type="button"
                onClick={() => addPreset(p)}
                disabled={tasks.some((x) => x.name.toLowerCase() === p.name.toLowerCase())}
                className="rounded-full border px-2.5 py-0.5 text-xs hover:bg-secondary disabled:opacity-40"
                data-testid={`stay-preset-${p.name.replace(/\W+/g, "-").toLowerCase()}`}
              >
                + {t(p.name)}
              </button>
            ))}
          </div>
          {tasks.length === 0 && <p className="text-xs italic text-muted-foreground">{t("No in-stay tasks yet — add one above.")}</p>}
          {tasks.map((x, i) => (
            <div key={x.id} className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-2 rounded-md border p-2" data-testid={`stay-task-row-${i}`}>
              <Input value={x.name} onChange={(e) => setTask(i, { name: e.target.value })} placeholder={t("Task")} className="h-8" data-testid={`stay-task-name-${i}`} />
              <label className="flex items-center gap-1 text-xs text-muted-foreground">
                {t("every")}
                <Input type="number" min="1" max="60" value={x.every_days} onChange={(e) => setTask(i, { every_days: Number(e.target.value) })} className="h-8 w-14 px-1.5 text-center" data-testid={`stay-task-days-${i}`} />
                {t("days")}
              </label>
              <label className="flex items-center gap-1 text-xs text-muted-foreground">
                <Input type="number" min="0" step="0.5" value={x.minutes} onChange={(e) => setTask(i, { minutes: Number(e.target.value) })} className="h-8 w-14 px-1.5 text-center" data-testid={`stay-task-min-${i}`} />
                {t("min")}
              </label>
              <div className="flex items-center gap-1">
                {stay && x.next_due && (
                  <label className="hidden items-center gap-1 text-[11px] text-muted-foreground sm:inline-flex" title={t("Next due — change it to move this task to another day")}>
                    <CalendarClock className="h-3 w-3" />
                    <Input
                      type="date"
                      value={x.next_override || x.next_due}
                      min={todayNZ()}
                      max={checkOut || undefined}
                      onChange={(e) => setTask(i, { next_override: e.target.value || null })}
                      className="h-7 w-[132px] px-1.5 text-[11px]"
                      data-testid={`stay-task-next-${i}`}
                    />
                  </label>
                )}
                <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-rose-600" onClick={() => setTasks((ts) => ts.filter((_, j) => j !== i))} data-testid={`stay-task-remove-${i}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>

        <DialogFooter className="gap-2 sm:justify-between">
          <div>
            {stay && (
              <Button variant="outline" className="gap-1.5 text-rose-700" onClick={() => remove.mutate()} disabled={remove.isPending} data-testid="stay-remove-btn">
                {remove.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} {t("Remove stay")}
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose} data-testid="stay-cancel-btn">{t("Cancel")}</Button>
            <Button onClick={() => save.mutate()} disabled={!valid || save.isPending} data-testid="stay-save-btn" className="gap-2">
              {save.isPending && <Loader2 className="h-4 w-4 animate-spin" />} {stay?.ended ? t("Reopen long stay") : t("Save long stay")}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
